import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Query } from '@/utils/react-query/Queries';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';
import ModalWrapper from '@/components/General/ModalWrapper';
import InputSection from '@/components/General/inputs/InputSection';

export default function ConversationInfoScreen() {
    const { conversationID } = useLocalSearchParams();
    const router = useRouter();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: conversation, isLoading } = Query.conversations.byId({id: conversationID, options: {enabled: !!conversationID}});

    if(isLoading){
        return (
            <ModalWrapper title="Conversation Info">
                <View style={styles.loaderContainer}>
                    <ActivityIndicator size="large"/>
                </View>
            </ModalWrapper>
        );
    }

    if(!conversation){
        return (
            <ModalWrapper title="Conversation Info">
                <Text style={styles.emptyText}>Conversation not found.</Text>
            </ModalWrapper>
        );
    }

    const isNamed = conversation.name && conversation.name != "*NONE*";
    const isRoleBased = conversation.membersByGroup === true;
    const peopleWithoutCurrentUser = conversation.People?.filter(p => p?.id != loggedInUser?.id);
    const createdBy = conversation.People?.find(p => p?.id == conversation.createdByID);

    const membersNames = peopleWithoutCurrentUser?.slice(0, 4)?.map((p, i) => {
        return i == 0
            ? p.firstName + (peopleWithoutCurrentUser?.length == 1 ? ` ${p.lastName}` : "")
            : i == peopleWithoutCurrentUser.length - 1
                ? " & " + p.firstName
                : (i == 3 ? " + " + (peopleWithoutCurrentUser?.length - 3) + " others" : ", " + p.firstName)
    }).join("");

    const canEdit = loggedInUser?.id == conversation.createdByID || loggedInUser?.isAdmin;

    return (
        <ModalWrapper
            title="Conversation Info"
            confirmText={canEdit ? "Edit" : undefined}
            onConfirm={canEdit ? () => {
                router.replace({pathname: "connect/conversations/create", params: {id: conversation.id}});
                return true;
            } : undefined}
        >
            <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
            <View style={styles.avatarSection}>
                {conversation.avatarPath
                    ? <Avatar size={72} profile={{id: conversation.id, key: conversation.avatarPath}} disableBorder directory="conversations/" onPress={() => {}}/>
                    : <View style={styles.avatarStack}>
                        {peopleWithoutCurrentUser?.slice(0, 4).map((p, i) => (
                            <View key={p.id} style={styles.stackItem(i * 25)}>
                                <Avatar size={48} profile={p} disableBorder onPress={() => {}}/>
                            </View>
                        ))}
                    </View>
                }
            </View>

            <InputSection title="Details">
                <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Name</Text>
                    <Text style={styles.detailValue}>{isNamed ? conversation.name : membersNames || "Unnamed"}</Text>
                </View>
                {createdBy && (
                    <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>Created By</Text>
                        <Text style={styles.detailValue}>{createdBy.firstName} {createdBy.lastName}</Text>
                    </View>
                )}
                {conversation.createdAt && (
                    <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>Created</Text>
                        <Text style={styles.detailValue}>{new Date(conversation.createdAt).toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'})}</Text>
                    </View>
                )}
            </InputSection>

            {isRoleBased && conversation.Roles?.length > 0 && (
                <InputSection title={`Roles (${conversation.Roles.length})`}>
                    <View style={styles.memberList}>
                        {conversation.Roles.map((role) => (
                            <View key={role.id} style={styles.memberRow}>
                                <View style={styles.roleIconBubble}>
                                    <Unicon name="people-outline" size={18} colorVariant="primary"/>
                                </View>
                                <View style={styles.memberInfo}>
                                    <Text style={styles.memberName} numberOfLines={1}>
                                        {role.name}
                                    </Text>
                                </View>
                            </View>
                        ))}
                    </View>
                </InputSection>
            )}

            <InputSection title={`Members (${conversation.People?.length || 0})`}>
                <View style={styles.memberList}>
                    {conversation.People?.map((person) => (
                        <View key={person.id} style={styles.memberRow}>
                            <Avatar size={36} profile={person} disableBorder onPress={() => router.push(`/people/${person.id}`)}/>
                            <View style={styles.memberInfo}>
                                <Text style={styles.memberName} numberOfLines={1}>
                                    {person.firstName} {person.lastName}
                                </Text>
                                {person.id == conversation.createdByID && (
                                    <Text style={styles.memberBadge}>Admin</Text>
                                )}
                                {person.id == loggedInUser?.id && (
                                    <Text style={styles.memberBadge}>You</Text>
                                )}
                                {person.isMuted && (
                                    <Unicon name="notifications-off-outline" size={15} colorVariant="textSecondary"/>
                                )}
                            </View>
                        </View>
                    ))}
                </View>
            </InputSection>
            </ScrollView>
        </ModalWrapper>
    );
}

const styles = StyleSheet.create(({colors, fonts, spacing}) => ({
    loaderContainer: {
        padding: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    emptyText: {
        fontSize: 15,
        color: colors.textSecondary,
        textAlign: 'center',
        padding: 30,
    },
    scrollContent: {
        paddingBottom: spacing.xl,
    },
    avatarSection: {
        alignItems: 'center',
        paddingTop: spacing.sm,
        paddingBottom: spacing.md,
    },
    avatarStack: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        height: 52,
        width: '100%',
    },
    stackItem: (offset) => ({
        transform: [{translateX: offset - 35}],
        position: 'absolute',
    }),
    detailRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 8,
    },
    detailLabel: {
        fontSize: 14,
        color: colors.textSecondary,
        fontFamily: fonts.body,
    },
    detailValue: {
        fontSize: 14,
        color: colors.text,
        fontFamily: fonts.body,
        flex: 1,
        textAlign: 'right',
    },
    memberList: {
        gap: 4,
    },
    memberRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 6,
        paddingHorizontal: 4,
        borderRadius: 10,
        backgroundColor: colors.hover,
    },
    memberInfo: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    memberName: {
        flex: 1,
        fontSize: 15,
        fontFamily: fonts.body,
        color: colors.text,
    },
    memberBadge: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.primary,
        backgroundColor: colors.bgPrimary,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 6,
        overflow: 'hidden',
    },
    roleIconBubble: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
}));
